import _, { last, sortBy } from 'lodash';
import {
  ConsolidatedDownPaymentRate,
  MinimalRate,
  Rate,
} from '../legos/financingTypes';
import {
  getDownPaymentAmountLimits,
  getFinanceAmountLimits,
} from './limitHelpers';
import { isDefined } from '../legos/utils/tsutils';
import {
  UPSTART_MAX_FINANCE_AMOUNT,
  UPSTART_MIN_FINANCE_AMOUNT,
} from '../constants';

function generateRandomString() {
  // Generate a random string about 11 characters long, e.g. "6ftdbmq63cr"
  return Math.random()
    .toString(32)
    .slice(2);
}

function roundFloat(value: number, precision = 2) {
  return parseFloat(value.toFixed(precision));
}

function incrementAmount(amount: number) {
  return roundFloat(amount + 0.01);
}
function decrementAmount(amount: number) {
  return roundFloat(amount - 0.01);
}

function determineMinFinanceAmount(currentRate: Rate, previousRate?: Rate) {
  if (
    // Sometimes DAPI sends a min finance amount of 0, but we should show the Upstart Min Amount
    typeof currentRate.minFinanceAmount === 'number' &&
    currentRate.minFinanceAmount === 0
  ) {
    return UPSTART_MIN_FINANCE_AMOUNT;
  }

  if (currentRate.minFinanceAmount) {
    return currentRate.minFinanceAmount;
  }

  // If there is a previous rate return the max finance amount of the previous rate + 1 cent
  return previousRate
    ? incrementAmount(previousRate.maxFinanceAmount)
    : UPSTART_MIN_FINANCE_AMOUNT;
}

/**
 * Build a list of rates grouped by term, including gaps between rates where
 * no offer is provided - represented by APR being set to null
 *
 * @param rates - List of rates with various terms
 * @returns object of sorted rates, mapped to term
 */
function buildRates(rates: Rate[]): Record<string, MinimalRate[]> {
  return _(rates)
    .chain()
    .sortBy(['months', 'maxDownPaymentAmount', 'maxFinanceAmount'])
    .groupBy('months')
    .mapValues(ratesByMonth =>
      ratesByMonth.reduce<MinimalRate[]>((result, rate, i, allRates) => {
        result.push({
          ...rate,
          minFinanceAmount: determineMinFinanceAmount(rate, allRates[i - 1]),
        });

        // If there is a gap between current and next rate range, then add a No Rate object
        const nextRate = allRates[i + 1];
        if (
          isDefined(nextRate?.minFinanceAmount) &&
          nextRate.maxDownPaymentAmount === rate.maxDownPaymentAmount &&
          nextRate.minFinanceAmount > incrementAmount(rate.maxFinanceAmount)
        ) {
          result.push({
            rateId: generateRandomString(),
            apr: null,
            months: rate.months,
            minFinanceAmount: incrementAmount(rate.maxFinanceAmount),
            maxFinanceAmount: decrementAmount(nextRate.minFinanceAmount),
            minDownPaymentAmount: rate.minDownPaymentAmount,
            maxDownPaymentAmount: rate.maxDownPaymentAmount,
          });
        }

        return result;
      }, []),
    )
    .value();
}

function isWithinDownPaymentRange(rate: MinimalRate, downPayment: number) {
  let isDownPaymentValid = true;
  if (isDefined(rate.minDownPaymentAmount)) {
    isDownPaymentValid =
      isDownPaymentValid && downPayment >= rate.minDownPaymentAmount;
  }
  if (isDefined(rate.maxDownPaymentAmount)) {
    isDownPaymentValid =
      isDownPaymentValid && downPayment <= rate.maxDownPaymentAmount;
  }
  return isDownPaymentValid;
}

/**
 * Filters rate map down a single rate per term returned as a flat list
 *
 * @param ratesByTerm - Map of rates keyed by term/months
 * @param financeAmount - Current finance amount
 * @param downPayment - Current down payment
 * @returns flat list of rates, one per term
 */
function filterTerms(
  ratesByTerm: Record<string, MinimalRate[]>,
  financeAmount: number,
  downPayment: number,
): MinimalRate[] {
  return Object.keys(ratesByTerm).map(
    term =>
      ratesByTerm[term].find(rate => {
        return (
          isWithinDownPaymentRange(rate, downPayment) &&
          financeAmount >= rate.minFinanceAmount &&
          financeAmount <= rate.maxFinanceAmount
        );
      }) ?? {
        rateId: generateRandomString(),
        apr: null,
        months: ratesByTerm[term][0].months,
        minFinanceAmount: UPSTART_MIN_FINANCE_AMOUNT,
        maxFinanceAmount: UPSTART_MAX_FINANCE_AMOUNT,
      },
  );
}

/**
 * Filters rates map down a single rate per finance amount range returned as a flat list
 *
 * If the current down payment yields no results, then return a list using the min/max down payment limits
 *
 * @param ratesByTerm - Map of rates keyed by term/months
 * @param term - Current term/months
 * @param downPayment - Current down payment
 * @returns flat list of rates, one per finance amount range
 */
function filterFinanceAmounts(
  ratesByTerm: Record<string, MinimalRate[]>,
  term: number,
  downPayment: number,
): MinimalRate[] {
  const validRatesForTerm = ratesByTerm[term.toString()] ?? [];

  const [minDownPaymentLimit, maxDownPaymentLimit] = getDownPaymentAmountLimits(
    validRatesForTerm,
  );

  if (downPayment < minDownPaymentLimit) {
    return validRatesForTerm.filter(rate =>
      isWithinDownPaymentRange(rate, minDownPaymentLimit),
    );
  }
  if (downPayment > maxDownPaymentLimit) {
    return validRatesForTerm.filter(rate =>
      isWithinDownPaymentRange(rate, maxDownPaymentLimit),
    );
  }

  return validRatesForTerm.filter(rate =>
    isWithinDownPaymentRange(rate, downPayment),
  );
}

/**
 * Filter and consolidate rates map down a single rate per down payment range returned as a flat list
 *
 * If the down payment will cause the finance amount to fall outside of the rate's finance amount range,
 * then adjust the down payment range to fit within the finance amount range.
 *
 * If there are down payment ranges with the same APR, then consolidate them into a single rate.
 *
 * @param ratesByTerm - Map of rates keyed by term/months
 * @param term - Current term/months
 * @param financeAmount - Current finance amount
 * @returns flat list of rates, one per down payment range
 */
function filterDownPayments(
  ratesByTerm: Record<string, MinimalRate[]>,
  term: number,
  financeAmount: number,
  currentDownPayment: number,
): ConsolidatedDownPaymentRate[] {
  // Reset finance amount without current down payment
  const baseFinanceAmount = financeAmount + currentDownPayment;

  const validRates =
    ratesByTerm[term.toString()]
      ?.filter(
        rate =>
          isDefined(rate.minDownPaymentAmount) &&
          isDefined(rate.maxDownPaymentAmount),
      )
      .map(rate => {
        const minFinanceAmount = roundFloat(
          baseFinanceAmount - (rate.maxDownPaymentAmount ?? 0),
        );
        const maxFinanceAmount = roundFloat(
          baseFinanceAmount - (rate.minDownPaymentAmount ?? 0),
        );

        // If both values are within the finance amount, return the rate unaltered
        if (
          minFinanceAmount >= rate.minFinanceAmount &&
          maxFinanceAmount <= rate.maxFinanceAmount
        ) {
          return rate;
        }

        // If the min value is out of bounds, but the max value is within the finance amount, then update the max down payment amount
        const shouldUpdateMaxDownPaymentAmount =
          minFinanceAmount < rate.minFinanceAmount &&
          maxFinanceAmount >= rate.minFinanceAmount;
        // If the max value is out of bounds, but the min value is within the finance amount, then update the min down payment amount
        const shouldUpdateMinDownPaymentAmount =
          maxFinanceAmount > rate.maxFinanceAmount &&
          minFinanceAmount <= rate.maxFinanceAmount;

        if (
          shouldUpdateMaxDownPaymentAmount ||
          shouldUpdateMinDownPaymentAmount
        ) {
          const maxDownPaymentAmount = shouldUpdateMaxDownPaymentAmount
            ? roundFloat(baseFinanceAmount - rate.minFinanceAmount)
            : rate.maxDownPaymentAmount;
          const minDownPaymentAmount = shouldUpdateMinDownPaymentAmount
            ? roundFloat(baseFinanceAmount - rate.maxFinanceAmount)
            : rate.minDownPaymentAmount;

          return {
            ...rate,
            maxDownPaymentAmount,
            minDownPaymentAmount,
          };
        }

        // All values are out of range
        return null;
      })
      .filter(isDefined) ?? [];

  const consolidatedRates = sortBy(validRates, 'maxDownPaymentAmount').reduce<
    ConsolidatedDownPaymentRate[]
  >((result, rate) => {
    const lastRate = last(result);
    let currentRate: ConsolidatedDownPaymentRate = {
      ...rate,
      rateIds: [rate.rateId],
    };

    // If the rate has the same APR as the previous rate, then update the max down payment and readd it
    if (lastRate && lastRate.apr === rate.apr) {
      result.pop(); // Remove last rate to readd with updated down payment
      currentRate = {
        ...lastRate,
        rateIds: [...lastRate.rateIds, rate.rateId],
        maxDownPaymentAmount: rate.maxDownPaymentAmount,
      };
    }

    result.push(currentRate);
    return result;
  }, []);

  return consolidatedRates;
}

/**
 * Finds currently matched rate based on where financeAmount lands within rate band
 *
 * This is inclusive of first and last bands even if the financeAmount lands
 * outside the limits for the rate.
 *   Example: a finance amount of 50_000 will select the last rate even if the
 *   overall max is only 30_000. This is a convenience to ensure there should
 *   be a rate returned
 *
 * @param input.rates - List of Rates sorted by finance amount bands (max finance amount)
 * @param input.financeAmount - Current finance amount
 * @returns current rate object
 */
function getSelectedRate({
  rates,
  financeAmount,
}: {
  rates: MinimalRate[];
  financeAmount: number;
}): MinimalRate | undefined {
  const [minFinanceAmountLimit, maxFinanceAmountLimit] = getFinanceAmountLimits(
    rates,
  );
  if (financeAmount < minFinanceAmountLimit) {
    return rates[0];
  }
  if (financeAmount > maxFinanceAmountLimit) {
    return last(rates);
  }
  return rates.find(rate => {
    return (
      financeAmount >= rate.minFinanceAmount &&
      financeAmount <= rate.maxFinanceAmount
    );
  });
}

export type ParseRatesResult = {
  /** @deprecated use financeAmountRates instead */
  relevantRates: MinimalRate[];
  financeAmountRates: MinimalRate[];
  termRates: MinimalRate[];
  downPaymentRates: ConsolidatedDownPaymentRate[];
  currentRate?: MinimalRate;
};

export function parseRates({
  rates,
  downPayment,
  financeAmount,
  months,
}: {
  rates: Rate[];
  downPayment: number;
  financeAmount: number;
  months?: number;
}) {
  const ratesByTerm = buildRates(rates ?? []);
  const termRates = filterTerms(ratesByTerm, financeAmount, downPayment);
  const financeAmountRates = months
    ? filterFinanceAmounts(ratesByTerm, months, downPayment)
    : [];
  const downPaymentRates = months
    ? filterDownPayments(ratesByTerm, months, financeAmount, downPayment)
    : [];

  const currentRate = getSelectedRate({
    financeAmount,
    rates: financeAmountRates,
  });

  return {
    relevantRates: financeAmountRates,
    financeAmountRates,
    termRates,
    downPaymentRates,
    currentRate,
  };
}
